import { useState, useEffect } from 'react';
import { useAuth } from './useAuth';

interface UsageStats {
  flowsAnalyzed: number;
  flowsUpdated: number;
  orgsConnected: number;
  healthGrade: string;
  averageScore: number;
  issuesIdentified: number;
  issuesResolved: number;
  improvementPercentage: number;
  issueReductionPercentage: number;
  firstSyncDate: string | null;
  latestSyncDate: string | null;
  hoursPerFlowAnalyzed: number;
  hoursPerFlowUpdated: number;
  totalSavings: number;
  totalHoursSaved: number;
  monthlySubscriptionCost: number;
  hourlyRate?: number;
  scoreHistory?: Array<{ date: string; score: number }>;
  issuesFixedHistory?: Array<{ date: string; count: number }>;
  weekdayEngagement?: Array<{ day: string; logins: number }>;
  platformUsage?: {
    salesforceDays: number;
    flowDocsDays: number;
    engagementRate: number;
    period: string;
  };
  lastMonthNewFlows?: number;
  lastMonthUpdatedFlows?: number;
  lastMonthHoursSaved?: number;
  lastMonthSavings?: number;
  periodLabel?: string;
}

interface UseUsageStatsResult {
  stats: UsageStats | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useUsageStats(timePeriod?: 'current_billing' | 'last_billing' | 'this_month' | 'last_month' | 'last_30_days'): UseUsageStatsResult {
  const { user } = useAuth();
  const [stats, setStats] = useState<UsageStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = async () => {
    if (!user) {
      setStats(null);
      setLoading(false);
      return;
    }

    try {
      setError(null);
      
      // Get auth token
      const { supabase } = await import('../services/supabaseClient');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('No authentication token');
      }

      const url = timePeriod ? `/api/subscription/usage-stats?period=${timePeriod}` : '/api/subscription/usage-stats';
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch usage stats: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Raw usage stats from API:', data);
      setStats(data);
    } catch (err) {
      console.error('Error fetching usage stats:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch usage statistics');
      
      // Set default stats on error
      setStats({
        flowsAnalyzed: 0,
        flowsUpdated: 0,
        orgsConnected: 0,
        healthGrade: 'N/A',
        averageScore: 0,
        issuesIdentified: 0,
        issuesResolved: 0,
        improvementPercentage: 0,
        issueReductionPercentage: 0,
        firstSyncDate: null,
        latestSyncDate: null,
        hoursPerFlowAnalyzed: 1.5,
        hoursPerFlowUpdated: 0.75,
        totalSavings: 0,
        totalHoursSaved: 0,
        monthlySubscriptionCost: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const refresh = async () => {
    setLoading(true);
    await fetchStats();
  };

  useEffect(() => {
    fetchStats();
  }, [user, timePeriod]);

  return {
    stats,
    loading,
    error,
    refresh
  };
}

// Utility function to format the health grade with color
export function getHealthGradeColor(grade: string): string {
  switch (grade) {
    case 'A':
    case 'A+':
      return 'text-green-600';
    case 'B':
    case 'B+':
      return 'text-blue-600';
    case 'C':
    case 'C+':
      return 'text-yellow-600';
    case 'D':
    case 'D+':
      return 'text-orange-600';
    case 'F':
      return 'text-red-600';
    default:
      return 'text-gray-600';
  }
}

// Utility function to get improvement trend
export function getImprovementTrend(percentage: number): {
  text: string;
  color: string;
  icon: 'up' | 'down' | 'neutral';
} {
  if (percentage > 10) {
    return {
      text: `${percentage}% improvement`,
      color: 'text-green-600',
      icon: 'up'
    };
  } else if (percentage > 0) {
    return {
      text: `${percentage}% improvement`,
      color: 'text-blue-600',
      icon: 'up'
    };
  } else if (percentage < -10) {
    return {
      text: `${Math.abs(percentage)}% decline`,
      color: 'text-red-600',
      icon: 'down'
    };
  } else if (percentage < 0) {
    return {
      text: `${Math.abs(percentage)}% decline`,
      color: 'text-orange-600',
      icon: 'down'
    };
  } else {
    return {
      text: 'No change',
      color: 'text-gray-600',
      icon: 'neutral'
    };
  }
}